import React, { useState, useRef, useEffect } from 'react';
import useFormTheme from '@/hooks/useFormTheme';
import FormThemeToggle from '@/components/FormThemeToggle';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Loader2, Check, ShieldCheck, Heart, HeartHandshake, ArrowRight, Phone, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import api, { saveSessionToken } from '@/utils/api';
import { getMetaAttribution, newMetaEventId, clearMetaAttribution } from '@/utils/metaAttribution';
import { BACKEND_URL } from '@/utils/backendUrl';
import ConsentNote from '@/components/ConsentNote';
import { isMetaWebview } from '@/utils/webview';
import { readQuizTheme } from '@/utils/quizTheme';

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001'; // test key fallback

// Common disposable email domains (subset — full list validated server-side)
const DISPOSABLE_DOMAINS = new Set([
  'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'temp-mail.org',
  'throwaway.email', 'yopmail.com', 'sharklasers.com', 'maildrop.cc',
  'trashmail.com', '10minutemail.com', 'fakeinbox.com', 'fakemail.net',
  'dispostable.com', 'spambox.us', 'getairmail.com', 'getnada.com',
  'burnermail.io', 'mohmal.com', 'tempinbox.com', 'mailnesia.com',
  'guerrillamail.de', 'guerrillamail.net', 'emailondeck.com',
  'temp-mail.io', 'throwaway.me', 'spamgourmet.com', 'mailcatch.com',
]);

const isDisposableEmail = (email) => {
  if (!email || !email.includes('@')) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return DISPOSABLE_DOMAINS.has(domain);
};

// ─── Shared split-layout shell (Figma "Create Account" 917:33 — form left, art right) ──
const FIELD_STYLE = { background: 'hsl(var(--input))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--foreground))', height: 52, borderRadius: 12, boxShadow: 'none' };
const PRIMARY_BTN = 'w-full h-12 rounded-xl px-5 py-3 text-base leading-6 font-medium hover:opacity-90';
const PRIMARY_STYLE = { background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' };
const LABEL_CLASS = 'text-sm leading-5 font-medium';
const LABEL_STYLE = { color: 'hsl(var(--foreground) / 0.85)' };

const AuthShell = ({ children }) => {
  const formTheme = useFormTheme();
  return (
  <div className="h-screen flex gap-8 p-4 lg:p-8 bg-background overflow-hidden">
    <FormThemeToggle theme={formTheme.theme} onToggle={formTheme.toggle} />
    <div className="w-full lg:flex-1 h-full overflow-y-auto px-2 sm:px-8 xl:px-[120px]">
      <div className="min-h-full flex flex-col items-center justify-center py-8">
        <div className="w-full max-w-[440px]">{children}</div>
        <p className="mt-12 text-base leading-6 text-center whitespace-nowrap" style={{ color: 'hsl(var(--muted-foreground))' }}>
          © 2026 ALL RIGHTS RESERVED
        </p>
      </div>
    </div>
    <div className="hidden lg:flex lg:flex-1 h-full items-start">
      <div className="w-full h-full rounded-3xl overflow-hidden" style={{ background: '#fea91a' }}>
        <img
          src="/auth-couple.webp"
          alt="Couple who found compatibility through SoulSathiya"
          className="w-full h-full object-cover object-bottom"
          loading="lazy"
          draggable={false}
        />
      </div>
    </div>
  </div>
  );
};

// ─── Journey Declaration Step ─────────────────────────────────────────────────
const JOURNEY_OPTIONS = [
  {
    type:    'matchmaking',
    icon:    Heart,
    color:   'hsl(43,82%,52%)',
    colorBg: 'hsla(43,82%,52%,0.08)',
    border:  'hsla(43,82%,52%,0.4)',
    title:   'I\'m looking for a life partner',
    sub:     'Single · Intentional Matchmaking',
    desc:    'Build your compatibility profile and get matched with seriously aligned partners.',
  },
  {
    type:    'insights',
    icon:    HeartHandshake,
    color:   'hsl(217,91%,60%)',
    colorBg: 'hsla(217,91%,60%,0.08)',
    border:  'hsla(217,91%,60%,0.4)',
    title:   'I\'m in a relationship / married',
    sub:     'Relationship Intelligence · Reports',
    desc:    'Explore deep compatibility reports and relationship insights — for you and your partner.',
  },
];

function JourneyDeclaration({ onSelect }) {
  // ~90% of ad-driven registrations choose matchmaking (Clarity, Aug 2026), and
  // a greyed-out Continue reads as "page broken" in slow in-app browsers — so
  // the majority option starts selected and the CTA is live from first paint.
  // The couple option stays fully visible and one tap away.
  const [selected, setSelected] = useState('matchmaking');

  return (
    <div className="w-full space-y-6">

      <div>
        <h1 className="font-heading text-[32px] sm:text-[40px] leading-tight sm:leading-[48px] font-medium tracking-[-0.4px] text-primary mb-1">What brings you here?</h1>
        <p className="text-lg leading-7 text-foreground">
          This helps us tailor your experience. Your journey type is kept private and cannot be changed later.
        </p>
      </div>

      {/* Options */}
      <div className="space-y-4">
        {JOURNEY_OPTIONS.map(({ type, icon: Icon, color, colorBg, border, title, sub, desc }) => {
          const isSelected = selected === type;
          return (
            <button
              key={type}
              type="button"
              onClick={() => setSelected(type)}
              className="w-full text-left rounded-2xl p-5 transition-all duration-200 focus:outline-none"
              style={{
                background:  isSelected ? colorBg : 'hsl(216,57%,13%)',
                border:      `2px solid ${isSelected ? color : 'hsl(216,58%,20%)'}`,
                boxShadow:   isSelected ? `0 0 24px ${colorBg}` : 'none',
              }}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: colorBg, border: `1px solid ${border}` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="font-semibold text-foreground text-sm">{title}</p>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      isSelected ? 'border-transparent' : 'border-muted-foreground/40'
                    }`} style={isSelected ? { background: color } : {}}>
                      {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                    </div>
                  </div>
                  <p className="text-xs font-medium mb-1.5" style={{ color }}>{sub}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        disabled={!selected}
        onClick={() => onSelect(selected)}
        className="w-full h-12 rounded-xl font-medium text-base flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        style={selected ? { background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' } : { background: 'hsl(var(--input))', color: 'hsl(var(--muted-foreground))', border: '1px solid hsl(var(--border))' }}
      >
        Continue
        <ArrowRight className="w-4 h-4" />
      </button>

      <p className="text-center text-base leading-6 font-medium">
        <span style={{ color: '#c6c6c6' }}>Already have an account? </span>
        <Link to="/login" className="text-primary underline">Sign in</Link>
      </p>
    </div>
  );
}

// ─── Main Register Page ───────────────────────────────────────────────────────
const RegisterPage = () => {
  const navigate = useNavigate();
  // Portrait-first funnel (/v2/me → /register?flow=portrait): singles here for
  // insights register up front (same details + mobile OTP as matchmaking), then
  // go straight to the 36Q instead of profile onboarding.
  const [searchParams] = useSearchParams();
  const portraitFlow = searchParams.get('flow') === 'portrait';
  // Arriving from the /quiz teaser: pick up the theme it surfaced (this tab only).
  const quizTheme = portraitFlow ? readQuizTheme('single') : null;
  // Pre-select insights if user came from CoupleEntryPage / InsightsLandingPage
  const [journeyType, setJourneyType] = useState(() => {
    if (portraitFlow) return 'matchmaking';
    const v = localStorage.getItem('ss_vertical');
    if (v === 'couple') { localStorage.removeItem('ss_vertical'); return 'insights'; }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'phone_otp'
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Pre-signup phone verification (verify BEFORE the account is created).
  // Email is NOT verified at registration — it is verified later via the
  // dashboard OTP gate (required before sending interests).
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [signupOtpSent, setSignupOtpSent] = useState(false);

  // ── Phone validation ──
  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) return /^91[6-9]\d{9}$/.test(digits);
    return /^[6-9]\d{9}$/.test(digits);
  };

  const normalisePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
    return `+91${digits}`;
  };

  // ── Pre-signup phone verification (no account exists yet) ──
  const handleSendSignupOtp = async () => {
    if (!validatePhone(formData.phone)) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/auth/phone/request-otp`,
        { phone: normalisePhone(formData.phone) },
        { withCredentials: true }
      );
      setSignupOtpSent(true);
      setResendCooldown(30);
      setOtpCode('');
      if (res.data?.dev_otp) toast.info(`Dev OTP: ${res.data.dev_otp}`, { duration: 8000 });
      else toast.success('OTP sent to your mobile number!');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Could not send OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleConfirmSignupOtp = async () => {
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/phone/confirm-otp`,
        { phone: normalisePhone(formData.phone), otp: otpCode },
        { withCredentials: true }
      );
      setPhoneVerified(true);
      setSignupOtpSent(false);
      toast.success('Mobile number verified ✓');
      // Escort forward: verification succeeded, so bring the next incomplete
      // field into view — a stranded "Verified" badge invites rage clicks.
      setTimeout(() => {
        const email = document.getElementById('email');
        const pwd = document.getElementById('password');
        const target = (email && !email.value) ? email
          : (pwd && !pwd.value) ? pwd
          : document.querySelector('[data-testid="register-submit-btn"]');
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (target?.tagName === 'INPUT') target.focus({ preventScroll: true });
      }, 150);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Incorrect OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Resend cooldown timer ──
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // ── Resume the OTP step after a page reload ──
  // The account is already created at this point; if the user refreshes (or
  // switches to their SMS app and comes back) we must NOT drop them back to a
  // blank form — that leads to "Email already registered" and a dead end.
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem('ss_pending_otp');
      if (pending) {
        const { phone } = JSON.parse(pending);
        if (phone) {
          setFormData(prev => ({ ...prev, phone }));
          setJourneyType('matchmaking');
          setStep('phone_otp');
        }
      }
    } catch { /* ignore */ }
  }, []);

  // ── Password strength checker ──
  const getPasswordStrength = (pw) => {
    if (!pw) return { score: 0, label: '', checks: {} };
    const checks = {
      length: pw.length >= 8,
      uppercase: /[A-Z]/.test(pw),
      lowercase: /[a-z]/.test(pw),
      number: /[0-9]/.test(pw),
      special: /[!@#$%^&*()_+\-=\[\]{}|;:'",.<>?/`~\\]/.test(pw),
    };
    const passed = Object.values(checks).filter(Boolean).length;
    const labels = ['', 'Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    return { score: passed, label: labels[passed] || '', checks };
  };

  const strength = getPasswordStrength(formData.password);
  const strengthColors = ['', 'bg-red-500', 'bg-red-500', 'bg-amber-500', 'bg-primary', 'bg-green-500'];
  const strengthTextColors = ['', 'text-red-400', 'text-red-400', 'text-amber-400', 'text-primary', 'text-green-400'];


  // ── Lightweight device fingerprint (no external library) ──
  const getDeviceFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('SoulSathiya-fp', 2, 2);
    const canvasHash = canvas.toDataURL().slice(-50);

    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || '',
      navigator.platform || '',
      canvasHash,
    ].join('|');

    // Simple hash
    let hash = 0;
    for (let i = 0; i < components.length; i++) {
      const chr = components.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0;
    }
    return 'fp_' + Math.abs(hash).toString(36);
  };

  // Render hCaptcha widget once the API script is loaded
  useEffect(() => {
    const renderCaptcha = () => {
      if (window.hcaptcha && captchaRef.current && !captchaRef.current.dataset.rendered) {
        window.hcaptcha.render(captchaRef.current, {
          sitekey: HCAPTCHA_SITE_KEY,
          theme: 'dark',
          callback: (token) => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(''),
          'error-callback': () => setCaptchaToken(''),
        });
        captchaRef.current.dataset.rendered = 'true';
      }
    };
    // hCaptcha script loads async — poll briefly if not ready
    if (window.hcaptcha) {
      renderCaptcha();
    } else {
      const id = setInterval(() => {
        if (window.hcaptcha) { clearInterval(id); renderCaptcha(); }
      }, 300);
      return () => clearInterval(id);
    }
  }, [journeyType]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = formData.full_name.trim();
    if (!trimmedName) {
      toast.error('Full name is required');
      return;
    }

    if (isDisposableEmail(formData.email)) {
      toast.error('Temporary or disposable email addresses are not allowed. Please use a permanent email.');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    if (!captchaToken) {
      toast.error('Please complete the security check');
      return;
    }

    if (journeyType === 'matchmaking') {
      if (!formData.phone.trim()) {
        toast.error('Mobile number is required for matchmaking');
        return;
      }
      if (!validatePhone(formData.phone)) {
        toast.error('Please enter a valid 10-digit Indian mobile number');
        return;
      }
      if (!phoneVerified) {
        toast.error('Please verify your mobile number with OTP first');
        return;
      }
    }

    setLoading(true);
    try {
      // Retrieve UTM data from localStorage if present
      let utmData = {};
      try {
        const stored = localStorage.getItem('utm_data');
        if (stored) {
          utmData = JSON.parse(stored);
        }
      } catch (e) {
        console.error('Failed to parse UTM data:', e);
      }

      // One id for both halves of this signup: the browser event below and the
      // server-side event Meta receives from our backend. Without a shared id
      // Meta would count the same registration twice.
      const metaEventId = newMetaEventId();

      const response = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        {
          full_name: trimmedName,
          email: formData.email,
          password: formData.password,
          terms_accepted: true,
          captcha_token: captchaToken,
          device_fingerprint: getDeviceFingerprint(),
          journey_type: journeyType,
          ...(journeyType === 'matchmaking' && { phone: normalisePhone(formData.phone) }),
          ...utmData,
          ...getMetaAttribution(),
          meta_event_id: metaEventId,
        },
        { withCredentials: true }
      );

      try {
        localStorage.removeItem('utm_data');
      } catch (e) {}
      clearMetaAttribution();

      saveSessionToken(response.data);

      // GTM / GA4 / Meta conversion trigger — fired from this custom event
      // instead of relying on URL/route changes.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'sign_up_complete' });
      // Meta signup conversion. Still no PII from the browser — the scrambled
      // email is added server-side. Our backend reports the same signup with
      // this same eventID, so Meta collapses the pair instead of counting two.
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration', {}, { eventID: metaEventId });
      }

      if (journeyType) localStorage.setItem('ss_journey_type', journeyType);

      // Apply referral code right at registration so attribution works in every
      // funnel (the portrait-first flow may never reach profile onboarding).
      // Fire-and-forget — never blocks signup; ProfileOnboarding's apply is a
      // safe no-op afterwards because the stored code is removed here.
      const refCode = localStorage.getItem('ss_referral_code');
      if (refCode) {
        api.post('/api/referral/apply', { code: refCode })
          .catch(() => { /* invalid or already-applied code — non-fatal */ })
          .finally(() => localStorage.removeItem('ss_referral_code'));
      }

      if (journeyType === 'matchmaking') {
        // Mobile was already verified during the form, so the account is created
        // verified — go straight to onboarding (no second OTP step).
        // Portrait-first funnel: the 36Q comes first; profile onboarding follows the report.
        toast.success('Account created! Welcome to SoulSathiya.', { duration: 3000 });
        navigate(portraitFlow ? '/onboarding/psychometric/v1' : '/onboarding/profile', { state: { user: response.data.user } });
      } else {
        // Email is verified later via the dashboard OTP gate — no detour here.
        toast.success('Account created! Welcome to SoulSathiya.', { duration: 3000 });
        navigate('/insights/dashboard', { state: { user: response.data.user } });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = (Array.isArray(detail) ? detail[0]?.msg : detail) || error?.message || 'Registration failed';
      // If the account already exists (e.g. user got stuck mid-verification and
      // tried again), guide them to login instead of leaving them at a dead end.
      if (/already registered|already exists|already in use/i.test(message)) {
        toast.error('This email is already registered. Taking you to login…');
        setTimeout(() => navigate('/login'), 1600);
      } else {
        toast.error(message);
      }
      setCaptchaToken('');
      if (window.hcaptcha && captchaRef.current) {
        try { window.hcaptcha.reset(); } catch (_) {}
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setOtpLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${BACKEND_URL}/api/auth/phone/verify-otp`,
        { phone: normalisePhone(formData.phone), otp: otpCode },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      try { sessionStorage.removeItem('ss_pending_otp'); } catch { /* ignore */ }
      toast.success('Mobile number verified! Welcome to SoulSathiya.', { duration: 3000 });
      navigate(portraitFlow ? '/onboarding/psychometric/v1' : '/onboarding/profile');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Incorrect OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${BACKEND_URL}/api/auth/phone/send-otp`,
        { phone: normalisePhone(formData.phone) },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success('OTP resent!');
      setResendCooldown(30);
      setOtpCode('');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Could not resend OTP. Please try again.');
    }
  };

  const handleGoogleSignup = () => {
    // The backend /google/start endpoint owns the OAuth redirect: direct
    // Google OAuth when configured, legacy Emergent broker otherwise.
    try {
      // Save journey type before redirect — survives the OAuth round-trip
      if (journeyType) localStorage.setItem('ss_journey_type', journeyType);
      const redirectUrl = window.location.origin + (portraitFlow ? '/onboarding/psychometric/v1' : '/onboarding/profile');
      window.location.href = `${BACKEND_URL}/api/auth/google/start?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      toast.error('Google sign-up could not start. Please try again or register with email.');
    }
  };

  // ── Step 2: Phone OTP Verification ──────────────────────────────────────
  if (step === 'phone_otp') {
    const maskedPhone = normalisePhone(formData.phone).replace(/(\+91)(\d{2})\d{6}(\d{2})/, '$1 $2******$3');
    return (
      <AuthShell>
        <div className="space-y-6">
          <div>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ background: 'hsla(44,100%,47%,0.12)', border: '1px solid hsla(44,100%,47%,0.4)' }}>
              <Phone className="w-6 h-6 text-primary" />
            </div>
            <h1 className="font-heading text-[32px] sm:text-[40px] leading-tight sm:leading-[48px] font-medium tracking-[-0.4px] text-primary mb-1">Verify Your Mobile</h1>
            <p className="text-lg leading-7 text-foreground">
              We sent a 6-digit code to <span className="font-medium">{maskedPhone}</span>
            </p>
          </div>

          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="otp" className={LABEL_CLASS} style={LABEL_STYLE}>Enter OTP</Label>
              <Input
                style={FIELD_STYLE}
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="6-digit code"
                className="text-center text-xl tracking-[0.4em] font-mono placeholder:text-muted-foreground"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
              />
            </div>

            <Button
              type="submit"
              className={PRIMARY_BTN} style={PRIMARY_STYLE}
              disabled={otpLoading || otpCode.length !== 6}
            >
              {otpLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
              ) : (
                'Verify & Continue'
              )}
            </Button>
          </form>

          <div className="text-center">
            {resendCooldown > 0 ? (
              <p className="text-sm" style={{ color: '#aaaaaa' }}>Resend OTP in {resendCooldown}s</p>
            ) : (
              <button
                type="button"
                onClick={handleResendOtp}
                className="text-base font-medium text-primary underline"
              >
                Resend OTP
              </button>
            )}
          </div>

          <p className="text-center text-base" style={{ color: '#c6c6c6' }}>
            Wrong number?{' '}
            <button
              type="button"
              onClick={() => { setStep('form'); setOtpCode(''); try { sessionStorage.removeItem('ss_pending_otp'); } catch { /* ignore */ } }}
              className="text-primary underline font-medium"
            >
              Go back
            </button>
          </p>
        </div>
      </AuthShell>
    );
  }

  // ── Step 0: Journey Declaration ──────────────────────────────────────────
  if (!journeyType) {
    return (
      <AuthShell>
        <JourneyDeclaration onSelect={(type) => {
          if (type === 'insights') {
            navigate('/couple');
          } else {
            setJourneyType(type);
          }
        }} />
      </AuthShell>
    );
  }

  // ── Step 1: Registration Form ─────────────────────────────────────────────
  const selectedJourney = JOURNEY_OPTIONS.find(o => o.type === journeyType);

  return (
    <AuthShell>
      <div className="space-y-3">

        <div>
          <h1 className="font-heading text-[32px] sm:text-[40px] leading-tight sm:leading-[48px] font-medium tracking-[-0.4px] text-primary mb-1">
            {portraitFlow ? 'Begin Your Relationship Portrait' : 'Create your Account'}
          </h1>
          {portraitFlow && (
            <p className="text-lg leading-7 text-foreground mb-2">
              Create your account to begin — 36 reflections, free snapshot, private &amp; secure.
            </p>
          )}
          {quizTheme && (
            <p className="text-base leading-6 text-foreground/80 mb-2">
              Your quick reflection leaned towards <span className="text-primary font-medium">{quizTheme}</span>.
              The Portrait shows how that plays out across all 8 dimensions.
            </p>
          )}
          {/* Journey context badge */}
          <div className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full text-xs font-medium"
            style={{ background: selectedJourney.colorBg, border: `1px solid ${selectedJourney.border}`, color: selectedJourney.color }}>
            <selectedJourney.icon className="w-3 h-3" />
            {portraitFlow ? 'Relationship Portrait · Free Snapshot' : selectedJourney.sub}
          </div>
        </div>

          {/* Google first — the low-effort path is offered BEFORE the form,
              not discovered after filling it. Hidden in Meta webviews where
              Google blocks OAuth. */}
          {!isMetaWebview() && (<>
          <button
            type="button"
            className="w-full flex gap-4 items-center justify-center px-[9px] py-3 rounded-xl hover:opacity-90 transition-opacity"
            style={{ background: '#f3f9fa' }}
            onClick={handleGoogleSignup}
            data-testid="google-signup-btn"
          >
            <img src="/ic-google.svg" alt="" aria-hidden="true" className="w-7 h-7" draggable={false} />
            <span className="text-base leading-none tracking-[0.16px]" style={{ color: '#313957' }}>Sign in with Google</span>
          </button>

          <div className="flex gap-4 items-center justify-center py-2.5 w-full">
            <span className="flex-1 h-px" style={{ background: '#555555' }} />
            <span className="text-base leading-none tracking-[0.16px]" style={{ color: '#aaaaaa' }}>Or register with email</span>
            <span className="flex-1 h-px" style={{ background: '#555555' }} />
          </div>
          </>)}

          <form onSubmit={handleSubmit} className="space-y-2.5">
            <div className="space-y-1">
              <Label htmlFor="full_name" className={LABEL_CLASS} style={LABEL_STYLE}>Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                style={FIELD_STYLE}
                  id="full_name"
                  type="text"
                  placeholder="Your full name"
                  className="pl-10"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="fullname-input"
                />
              </div>
            </div>

            {journeyType === 'matchmaking' && (
              <div className="space-y-1">
                <Label htmlFor="phone" className={LABEL_CLASS} style={LABEL_STYLE}>Mobile Number <span className="text-red-400">*</span></Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                style={FIELD_STYLE}
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      placeholder="10-digit mobile number"
                      className="pl-10"
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({ ...formData, phone: e.target.value });
                        if (phoneVerified || signupOtpSent) { setPhoneVerified(false); setSignupOtpSent(false); }
                      }}
                      disabled={phoneVerified}
                      required
                    />
                  </div>
                  {phoneVerified ? (
                    <div className="flex items-center gap-1 px-3 text-green-400 text-sm font-medium whitespace-nowrap">
                      <Check className="w-4 h-4" /> Verified
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="whitespace-nowrap"
                      onClick={handleSendSignupOtp}
                      disabled={otpLoading || !validatePhone(formData.phone) || resendCooldown > 0}
                    >
                      {otpLoading ? (
                        <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>
                      ) : resendCooldown > 0 ? `Resend ${resendCooldown}s` : (signupOtpSent ? 'Resend OTP' : 'Verify with OTP')}
                    </Button>
                  )}
                </div>

                {formData.phone && !validatePhone(formData.phone) && !phoneVerified && (
                  <p className="text-xs text-red-400 mt-1">
                    Enter a valid Indian mobile number (10 digits, starting with 6–9)
                  </p>
                )}

                {signupOtpSent && !phoneVerified && (
                  <>
                    <div className="flex gap-2 mt-2">
                      <Input
                style={FIELD_STYLE}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="Enter 6-digit OTP"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        autoFocus
                      />
                      <Button
                        type="button"
                        className="whitespace-nowrap"
                        onClick={handleConfirmSignupOtp}
                        disabled={otpLoading || otpCode.length !== 6}
                      >
                        {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      OTP sent by SMS to {normalisePhone(formData.phone)} — it arrives within a minute.
                    </p>
                  </>
                )}
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="email" className={LABEL_CLASS} style={LABEL_STYLE}>Email <span className="text-red-400">*</span></Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                style={FIELD_STYLE}
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>
              {isDisposableEmail(formData.email) && (
                <p className="text-xs text-red-400 mt-1">
                  Disposable email addresses are not allowed. Please use your real email.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className={LABEL_CLASS} style={LABEL_STYLE}>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                style={FIELD_STYLE}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password strength indicator */}
              {formData.password && (
                <div className="space-y-2 pt-1">
                  {/* Strength bar */}
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          i <= strength.score ? strengthColors[strength.score] : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${strengthTextColors[strength.score]}`}>
                      {strength.label}
                    </span>
                  </div>
                  {/* Requirement checklist */}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                    {[
                      { key: 'length', label: '8+ characters' },
                      { key: 'uppercase', label: 'Uppercase (A-Z)' },
                      { key: 'lowercase', label: 'Lowercase (a-z)' },
                      { key: 'number', label: 'Number (0-9)' },
                      { key: 'special', label: 'Special (!@#$)' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-1.5">
                        {strength.checks[key] ? (
                          <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
                        ) : (
                          <div className="w-3 h-3 rounded-full border border-muted-foreground/30 flex-shrink-0" />
                        )}
                        <span className={`text-[11px] ${strength.checks[key] ? 'text-green-400' : 'text-muted-foreground/60'}`}>
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm-password removed (Sep 2026): the eye toggle already lets
                users check what they typed, and Clarity showed the extra field
                drawing 122 taps of pure friction on mobile in-app browsers. */}

            {/* Terms acceptance */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <button
                type="button"
                onClick={() => setTermsAccepted(!termsAccepted)}
                className={`w-5 h-5 mt-0.5 rounded border flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
                  termsAccepted
                    ? 'bg-primary border-primary'
                    : 'border-muted-foreground/40 group-hover:border-primary/60'
                }`}
              >
                {termsAccepted && <Check className="w-3.5 h-3.5 text-primary-foreground" />}
              </button>
              <span className="text-xs text-muted-foreground leading-relaxed">
                I agree to the{' '}
                <Link to="/terms" className="text-primary hover:underline" target="_blank" onClick={e => e.stopPropagation()}>
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary hover:underline" target="_blank" onClick={e => e.stopPropagation()}>
                  Privacy Policy
                </Link>
              </span>
            </label>

            {/* hCaptcha */}
            <div className="flex flex-col items-center gap-2">
              <div ref={captchaRef} data-testid="hcaptcha-widget" />
              {captchaToken && (
                <div className="flex items-center gap-1 text-xs text-green-400">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </div>
              )}
            </div>

            {/* Kept clickable even when prerequisites are missing — a tap then
                toasts exactly what's left (terms, captcha, OTP…). A silently
                dead button reads as "site broken" and invites rage clicks. */}
            <Button
              type="submit"
              className={`${PRIMARY_BTN} ${(!termsAccepted || isDisposableEmail(formData.email) || (journeyType === 'matchmaking' && !phoneVerified)) ? 'opacity-60' : ''}`}
              style={PRIMARY_STYLE}
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account...</>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <ConsentNote action="creating an account" />

          <p className="text-center text-base leading-6 font-medium">
            <span style={{ color: '#c6c6c6' }}>Already have an account? </span>
            <Link to="/login" className="text-primary underline">Sign in</Link>
          </p>
      </div>
    </AuthShell>
  );
};

export default RegisterPage;
